import Intercom from '@/components/intercom/intercom';
import { MeProvider, useMe } from '@/context/me/me.context';
import useAnalyticsPageViews from '@/hooks/use-analytics-view-page';
import { useHasActiveMembership } from '@/hooks/use-has-active-membership';
import { useGetFooterIsVisible } from '@/queries/layout.queries';
import { Flex } from '@cmpsr/components';
import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import FooterDashboard from '../dashboard/footer-dashboard/footer-dashboard';
import { ROUTE_SIGN_IN, ROUTE_ACCOUNT } from '@/constants/routes';
import authService from '@/services/auth.service';
import { getRoute } from '@/utils';

function AuthGuard({ children }: { children: React.ReactNode }): React.ReactNode {
  const { data: me } = useMe();
  const location = useLocation();
  const { isActive, isLoading: isMembershipLoading } = useHasActiveMembership(me);
  const loggingOutRef = useRef(false);

  // Hard reload (not soft navigate) after logout so AuthKit fully re-initializes.
  // AuthKit's signOut({ navigate: false }) clears the cookie but leaves
  // useAuth().user populated in React state — a soft navigate to /sign-in would
  // let WorkosSignInRedirect re-register the singleton from that stale user and
  // bounce back to /dashboard, producing an infinite loop.
  useEffect(() => {
    if (me || loggingOutRef.current) return;
    loggingOutRef.current = true;
    authService.logout().finally(() => {
      window.location.href = getRoute(ROUTE_SIGN_IN);
    });
  }, [me]);

  if (!me) {
    return null;
  }

  // Membership is derived from catch-backend's subscription (source of truth),
  // falling back to /me's VIP flag when no Stripe subscription exists.
  if (!isMembershipLoading && !isActive && !location.pathname.startsWith(ROUTE_ACCOUNT)) {
    return <Navigate to={ROUTE_ACCOUNT} replace />;
  }

  return children;
}

export default function MainLayout(): React.ReactElement {
  const { data: isFooterVisible } = useGetFooterIsVisible();
  useAnalyticsPageViews();

  return (
    <Flex flexDirection="column" minHeight="100vh" background="background-page">
      <MeProvider>
        <AuthGuard>
          <Outlet />
          <Intercom />
          <ScrollRestoration />
        </AuthGuard>
      </MeProvider>
      {isFooterVisible && <FooterDashboard />}
    </Flex>
  );
}
